// eslint-disable-next-line simple-import-sort/imports
import { useRouter } from 'next/router'
import * as React from 'react'
import { toast } from 'sonner'

import { RichTextareaHandle } from '@/components/custom/rich-textarea'
import { MESSAGE } from '@/constant/message.constant'
import { getAttachmentPlaceholderContent, hasUserMessageText } from '@/utils/chat-message-content'
import {
  Conversation,
  ConversationParticipant,
  ConversationType,
  DeleteMessageScope,
  Message,
  MessageType,
  PinnedMessage,
} from '@/types/chat.type'
import { UserAuth } from '@/types/user.type'

const VIDEO_FILE_EXTENSIONS = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.m4v']

// User interface for chat participants
interface ChatUser {
  id: number
  firstName: string
  lastName: string
  email?: string
  avatar?: string
  profileId?: string
}

// Message sender interface
interface MessageSender {
  id: number
  profileId?: string
}

// Search result message interface
interface SearchResultMessage {
  id: number
  content: string
  sender: { id: number; firstName: string; lastName: string; avatar?: string }
  createdAt: string
  conversationId: number
}

// Chat state interface
interface ChatStateInterface {
  isOpen: boolean
  newMessage: string
  selectedConversationId: number | undefined
  showNewConversationDialog: boolean
  showMessageSearch: boolean
  showUserSearch: boolean
  showSearchDialog: boolean
  searchQuery: string
  pinnedMessages: PinnedMessage[]
  isPinnedMessagesExpanded: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredConversations: any[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filteredMessages: any[]
  searchResults: {
    users: Array<{ id: number; firstName: string; lastName: string; email?: string; avatar?: string }>
    messages: Array<{
      id: number
      content: string
      sender: { id: number; firstName: string; lastName: string; avatar?: string }
      createdAt: string
      conversationId: number
    }>
    conversations: Array<{
      id: number
      name: string
      description?: string
      avatar?: string
      type: string
      lastMessageAt?: string
      lastMessageContent?: string
      participants: Array<{
        id: number
        firstName: string
        lastName: string
        email?: string
        avatar?: string
      }>
    }>
  }
  editingMessageId: number | null
  editingConversationId: number | null
  editingConversationName: string
  showEditConversationDialog: boolean
  editMessageContent: string
  newConversationName: string
  selectedUsers: Array<{
    id: number
    firstName: string
    lastName: string
    email?: string
    avatar?: string
    profileId?: string
  }>
  pendingTargetUserId: number | undefined
  targetUserName: string
  targetUserAvatar: string
  hasCheckedExistingConversation: boolean
  jobName: string
  deletingMessageId: number | null
  showDeleteConfirmDialog: boolean
  isDeleting: boolean
  deletingConversationId: number | null
  showDeleteConversationDialog: boolean
  isDeletingConversation: boolean
  showCreateGroupChatDialog: boolean
  selectedFile: File | null
  isUploadingFile: boolean
  previousMessageCount: number
  isMobileView: boolean
  showMobileConversationList: boolean
  isConversationRead: boolean
  // Reply state
  replyingToMessage: {
    id: number
    content: string
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
    type: string
    fileUrl?: string
    fileName?: string
  } | null
  // Setters
  setIsOpen: (isOpen: boolean) => void
  setNewMessage: (message: string) => void
  setSelectedConversationId: (id: number | undefined) => void
  setShowNewConversationDialog: (show: boolean) => void
  setShowMessageSearch: (show: boolean) => void
  setShowUserSearch: (show: boolean) => void
  setShowSearchDialog: (show: boolean) => void
  setSearchQuery: (query: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilteredConversations: (conversations: any[]) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFilteredMessages: (messages: any[]) => void
  setSearchResults: (results: {
    users: Array<{ id: number; firstName: string; lastName: string; email?: string; avatar?: string }>
    messages: Array<{
      id: number
      content: string
      sender: { id: number; firstName: string; lastName: string; avatar?: string }
      createdAt: string
      conversationId: number
    }>
    conversations: Array<{
      id: number
      name: string
      description?: string
      avatar?: string
      type: string
      lastMessageAt?: string
      lastMessageContent?: string
      participants: Array<{
        id: number
        firstName: string
        lastName: string
        email?: string
        avatar?: string
      }>
    }>
  }) => void
  setEditingMessageId: (id: number | null) => void
  setEditingConversationId: (id: number | null) => void
  setEditingConversationName: (name: string) => void
  setShowEditConversationDialog: (show: boolean) => void
  setEditMessageContent: (content: string) => void
  setNewConversationName: (name: string) => void
  setSelectedUsers: (
    users: Array<{ id: number; firstName: string; lastName: string; email?: string; avatar?: string }>
  ) => void
  setPendingTargetUserId: (id: number | undefined) => void
  setTargetUserName: (name: string) => void
  setTargetUserAvatar: (avatar: string) => void
  setHasCheckedExistingConversation: (checked: boolean) => void
  setJobName: (name: string) => void
  setDeletingMessageId: (id: number | null) => void
  setShowDeleteConfirmDialog: (show: boolean) => void
  setIsDeleting: (deleting: boolean) => void
  setShowDeleteConversationDialog: (show: boolean) => void
  setDeletingConversationId: (id: number | null) => void
  setIsDeletingConversation: (deleting: boolean) => void
  setShowCreateGroupChatDialog: (show: boolean) => void
  setSelectedFile: (file: File | null) => void
  setIsUploadingFile: (uploading: boolean) => void
  setPreviousMessageCount: (count: number) => void
  setIsMobileView: (isMobile: boolean) => void
  setShowMobileConversationList: (show: boolean) => void
  setIsConversationRead: (isRead: boolean) => void
  setReplyingToMessage: (message: ChatStateInterface['replyingToMessage']) => void
  setPinnedMessages: (messages: PinnedMessage[]) => void
  setIsPinnedMessagesExpanded: (expanded: boolean) => void
}

// Chat context interface
interface ChatContextInterface {
  conversations: Conversation[]
  conversation: Conversation | null
  messages: Message[]
  unreadCounts: Record<number, number>
  sendTypingIndicator: (isTyping: boolean, skipApiOnStop?: boolean) => void
  markConversationAsRead: (conversationId: number) => Promise<void>
  createConversation: (participantIds: number[], name?: string) => Promise<Conversation | undefined>
  createGroupChat: (
    name: string,
    description: string,
    participantIds: number[],
    type?: ConversationType,
    addAllUsers?: boolean
  ) => Promise<Conversation | undefined>
  reloadConversations: () => Promise<void>
  editMessage: (messageId: number, content: string) => Promise<void>
  deleteMessage: (messageId: number, scope: DeleteMessageScope) => Promise<void>
  deleteConversation: (conversationId: number, permanent?: boolean) => Promise<void>
  pinConversation: (conversationId: number) => Promise<void>
  unpinConversation: (conversationId: number) => Promise<void>
  pinMessage: (messageId: number, conversationId: number) => Promise<void>
  unpinMessage: (messageId: number, conversationId: number) => Promise<void>
  getPinnedMessages: (conversationId: number) => Promise<PinnedMessage[]>
  searchMessages: (query: string) => Promise<Message[]>
  updateConversation: (conversationId: number, updates: { name: string }) => Promise<void>
  decreaseUnreadCountByConversation: (conversationId: number, decreaseBy: number) => void
  loadPreviousMessages: () => void
  sendMessage: (content: string) => Promise<void>
  sendMessageWithFile: (content: string, file: File) => Promise<void>
  sendReplyMessage: (content: string, repliedMessageId: number) => Promise<void>
  setConversationId: (conversationId: number) => void
  loadConversations: () => Promise<void>
  isCreatingConversation: boolean
  isLoading: boolean
  debouncedLoadConversations: () => void
  searchConversations: (query: string) => Promise<Conversation[]>
  pendingScrollToMessage: { messageId: number; targetConversationId?: number } | null
}

interface ChatActionsProps {
  chatState: ChatStateInterface
  chatContext: ChatContextInterface
  authUser: UserAuth | null
  scrollManager?: {
    scrollToBottomInstant: () => void
    prepareConversationBottomScroll: () => void
    beginMessageNavigationScroll: () => void
    scrollToMessage: (messageId: number) => void
    scrollToMessageWithDelay: (messageId: number, delay?: number) => void
    scrollToConversation: (conversationId: number, conversationType?: string) => void
  }
}

export const useChatActions = ({ chatState, chatContext, authUser, scrollManager }: ChatActionsProps) => {
  const router = useRouter()
  const inputRef = React.useRef<RichTextareaHandle>(null)
  const hasVideoFileExtension = React.useCallback((fileName?: string) => {
    if (!fileName) return false
    const lowerFileName = fileName.toLowerCase()
    return VIDEO_FILE_EXTENSIONS.some(extension => lowerFileName.endsWith(extension))
  }, [])
  const isParticipantActive = React.useCallback((participant: ConversationParticipant) => {
    return !participant.status || participant.status === 'active'
  }, [])

  // Close chat
  const closeChat = React.useCallback(() => {
    if (!(chatState.pendingTargetUserId && (chatContext.isCreatingConversation || chatContext.isLoading))) {
      chatState.setIsOpen(false)
      chatState.setHasCheckedExistingConversation(false)
      chatState.setPendingTargetUserId(undefined)
      chatState.setTargetUserName('')
      chatState.setTargetUserAvatar('')
      chatState.setJobName('')

      // Reset search state when closing chat
      chatState.setSearchQuery('')
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      chatState.setShowSearchDialog(false)
    }
  }, [chatState, chatContext])

  // Handle click on message sender to open profile
  const handleMessageSenderClick = React.useCallback(
    (sender: MessageSender) => {
      const profileId = sender.profileId || sender.id.toString()
      router.push(`/user/${profileId}`)
      // Close chat popup after navigating to profile
      closeChat()
    },
    [router, closeChat]
  )

  // Helper function to find existing conversation with a specific user
  const findExistingConversation = React.useCallback(
    (targetUserId: number) => {
      if (!targetUserId || !chatContext.conversations || chatContext.conversations.length === 0) {
        return undefined
      }

      return chatContext.conversations.find((conv: Conversation) => {
        if (!conv.participants || conv.participants.length !== 2) {
          return false
        }

        const activeParticipants = conv.participants.filter((participant: ConversationParticipant) =>
          isParticipantActive(participant)
        )

        if (activeParticipants.length !== 2) {
          return false
        }

        const hasTargetUser = activeParticipants.some(
          (participant: ConversationParticipant) => participant.user.id === targetUserId
        )
        const hasCurrentUser = activeParticipants.some(
          (participant: ConversationParticipant) => participant.user.id === authUser?.id
        )

        return hasTargetUser && hasCurrentUser
      })
    },
    [chatContext.conversations, authUser?.id, isParticipantActive]
  )

  // Get targeted user for conversation (first other participant)
  const getTargetedUser = React.useCallback(
    (conversation: Conversation) => {
      if (!conversation.participants || conversation.participants.length === 0) {
        return null
      }

      const otherParticipants = conversation.participants.filter(
        (participant: ConversationParticipant) =>
          participant.user.id !== authUser?.id && isParticipantActive(participant)
      )

      if (otherParticipants.length === 0) {
        return null
      }

      return otherParticipants[0]
    },
    [authUser?.id, isParticipantActive]
  )

  // Format conversation title by participant names (excluding current user)
  const getConversationTitle = React.useCallback(
    (conversation: Conversation) => {
      // Only show conversation name if it exists and is not empty
      if (conversation.name && conversation.name.trim()) {
        return conversation.name
      }

      if (!conversation.participants || conversation.participants.length === 0) {
        // Ensure targetUserName has a meaningful value, with fallbacks
        if (chatState.targetUserName && chatState.targetUserName.trim()) {
          return chatState.targetUserName
        }
        if (chatState.jobName && chatState.jobName.trim()) {
          return `${chatState.jobName}`
        }
        return `Messages`
      }

      const otherParticipants = conversation.participants.filter(
        (participant: ConversationParticipant) =>
          participant.user.id !== authUser?.id && isParticipantActive(participant)
      )

      if (otherParticipants.length === 0) {
        return 'You'
      }

      if (otherParticipants.length === 1) {
        const participant = otherParticipants[0]
        return `${participant.user.firstName} ${participant.user.lastName}`
      }

      if (otherParticipants.length === 2) {
        const [first, second] = otherParticipants
        return `${first.user.firstName} ${first.user.lastName}, ${second.user.firstName} ${second.user.lastName}`
      }

      const [first, second] = otherParticipants
      return `${first.user.firstName} ${first.user.lastName}, ${second.user.firstName} ${second.user.lastName} +${otherParticipants.length - 2}`
    },
    [authUser?.id, chatState.targetUserName, chatState.jobName, isParticipantActive]
  )

  // Generate chat title based on context
  const getChatTitle = React.useCallback(() => {
    if (chatState.jobName) {
      return chatState.jobName
    }

    // If we have a selected conversation, use its title
    if (chatState.selectedConversationId && chatContext.conversation) {
      return getConversationTitle(chatContext.conversation)
    }

    // If we have a target user name, use it
    if (chatState.targetUserName) {
      return chatState.targetUserName
    }

    return 'Messages'
  }, [
    chatState.jobName,
    chatState.selectedConversationId,
    chatContext.conversation,
    chatState.targetUserName,
    getConversationTitle,
  ])

  // Format timestamp
  const formatTimestamp = React.useCallback((timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInDays < 7) return `${diffInDays}d ago`

    return date.toLocaleDateString()
  }, [])

  // Render file message - returns message data for rendering in components
  const renderFileMessage = React.useCallback((message: Message) => {
    if ((message.type === MessageType.FILE || message.type === MessageType.IMAGE || message.type === MessageType.VIDEO) && message.fileUrl) {
      const isVideo =
        message.type === MessageType.VIDEO ||
        !!message.fileType?.startsWith('video/') ||
        hasVideoFileExtension(message.fileName)
      return {
        type: message.type,
        fileUrl: message.fileUrl,
        fileName: message.fileName,
        fileSize: message.fileSize,
        content: message.content,
        isImage: message.type === MessageType.IMAGE,
        isVideo,
      }
    }
    return null
  }, [hasVideoFileExtension])

  // Insert emoji
  const insertEmoji = React.useCallback(
    (emoji: string) => {
      const currentMessage = chatState.newMessage
      chatState.setNewMessage(currentMessage + emoji)
    },
    [chatState]
  )

  // Insert custom emoji
  const insertCustomEmoji = React.useCallback(
    (emoji: { id: number; name: string; imageUrl: string }) => {
      const currentMessage = chatState.newMessage
      const newMessage = `${currentMessage} <img src="${emoji.imageUrl}" alt=":${emoji.name}:" style="width:24px;height:24px;display:inline-block;" />`
      chatState.setNewMessage(newMessage)
    },
    [chatState]
  )

  // Handle typing
  const handleTyping = React.useCallback(
    (html: string) => {
      chatState.setNewMessage(html)

      if (chatState.selectedConversationId || chatState.pendingTargetUserId) {
        const plainText = html
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim()
        if (plainText.length > 0) {
          chatContext.sendTypingIndicator(true)
        } else {
          chatContext.sendTypingIndicator(false)
        }
      }
    },
    [chatState, chatContext]
  )

  // Handle input focus
  const handleInputFocus = React.useCallback(() => {
    // Check if there's an unread count for the current conversation
    if (chatState.selectedConversationId) {
      const unreadCount = chatContext.unreadCounts[chatState.selectedConversationId] || 0

      // If there are unread messages, mark the conversation as read
      if (unreadCount > 0) {
        chatContext.markConversationAsRead(chatState.selectedConversationId)
      }
    }
  }, [chatState.selectedConversationId, chatContext])

  // Handle conversation selection
  const handleSelectConversation = React.useCallback(
    (conversationId: number) => {
      chatState.setSelectedConversationId(conversationId)

      // Auto-set isConversationRead to true for desktop view
      chatState.setIsConversationRead(true)

      // Inform chat context about the active conversation
      if (chatContext?.setConversationId) {
        chatContext.setConversationId(conversationId)
      }

      const isPendingMessageNavigation =
        chatContext.pendingScrollToMessage &&
        (!chatContext.pendingScrollToMessage.targetConversationId ||
          chatContext.pendingScrollToMessage.targetConversationId === conversationId)

      if (isPendingMessageNavigation) {
        scrollManager?.beginMessageNavigationScroll()
      } else {
        scrollManager?.prepareConversationBottomScroll()
      }

      // Load pinned messages for the conversation
      if (chatContext?.getPinnedMessages) {
        chatContext.getPinnedMessages(conversationId).then((pinnedMessages: PinnedMessage[]) => {
          if (chatState.setPinnedMessages) {
            chatState.setPinnedMessages(pinnedMessages)
          }
        })
      }

      // Scroll the sidebar list to the selected conversation's position
      if (scrollManager) {
        // Determine the conversation type for correct tab targeting
        const conversation = chatContext.conversations.find(conv => conv.id === conversationId)
        const conversationType = conversation?.type

        // Delay to ensure the list has rendered/highlighted the selected item
        setTimeout(() => {
          scrollManager.scrollToConversation(conversationId, conversationType)
        }, 100)
      }

      // Reset search state when selecting a conversation
      chatState.setSearchQuery('')
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      chatState.setShowSearchDialog(false)

      // Conversation selection handling
    },
    [chatState, chatContext, scrollManager]
  )

  // Handle create conversation
  const handleCreateConversation = React.useCallback(async () => {
    if (chatState.selectedUsers.length === 0) {
      toast.error('Please select at least one user')
      return
    }

    try {
      const participantIds = chatState.selectedUsers.map((user: ChatUser) => user.id)
      const newConversation = await chatContext.createConversation(participantIds)

      // Auto-select the newly created conversation
      if (newConversation) {
        chatState.setSelectedConversationId(newConversation.id)
      }

      // Reset form
      chatState.setSelectedUsers([])
      chatState.setShowNewConversationDialog(false)

      toast.success('Conversation created successfully!')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create conversation:', error)
      toast.error('Failed to create conversation. Please try again.')
    }
  }, [chatState, chatContext])

  // Handle user selection for new conversation
  const handleUserSelect = React.useCallback(
    (user: ChatUser) => {
      const currentUsers = chatState.selectedUsers
      const newUsers = [...currentUsers, user]
      chatState.setSelectedUsers(newUsers)
    },
    [chatState]
  )

  // Handle user removal from new conversation
  const handleUserRemove = React.useCallback(
    (userId: number) => {
      const currentUsers = chatState.selectedUsers
      const filteredUsers = currentUsers.filter((user: ChatUser) => user.id !== userId)
      chatState.setSelectedUsers(filteredUsers)
    },
    [chatState]
  )

  // Handle user profile navigation from search results
  const handleUserProfileNavigation = React.useCallback(
    (user: ChatUser) => {
      // Clear all search-related state
      chatState.setSearchQuery('')
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      chatState.setShowSearchDialog(false)

      // Navigate to user profile
      if (user.profileId) {
        router.push(`/user/${user.profileId}`)
      } else {
        // Only fallback to user ID if profileId is truly not available
        router.push(`/user/${user.id}`)
      }
    },
    [router, chatState]
  )

  // Handle user selection from search to start new conversation
  const handleUserSearchSelect = React.useCallback(
    (user: ChatUser) => {
      chatState.setShowUserSearch(false)
      chatState.setIsOpen(true)
      chatState.setPendingTargetUserId(user.id)
      // Note: targetUserName is kept for display purposes but not used for conversation names
      chatState.setTargetUserName(`${user.firstName} ${user.lastName}`)
      chatState.setTargetUserAvatar(user.avatar || '')
      chatState.setHasCheckedExistingConversation(false)

      // Reset search state when selecting a user
      chatState.setSearchQuery('')
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      chatState.setShowSearchDialog(false)
    },
    [chatState]
  )

  // Handle starting message edit
  const handleStartEditMessage = React.useCallback(
    (messageId: number, currentContent: string) => {
      const message = chatContext.messages.find((msg: Message) => msg.id === messageId)
      if (!message) return

      if (message.sender.id !== authUser?.id) {
        toast.error('You can only edit your own messages')
        return
      }

      if (message.isSystem) {
        toast.error('System messages cannot be edited')
        return
      }

      chatState.setEditingMessageId(messageId)
      chatState.setEditMessageContent(currentContent)
    },
    [chatContext.messages, authUser?.id, chatState]
  )

  // Handle canceling message edit
  const handleCancelEditMessage = React.useCallback(() => {
    chatState.setEditingMessageId(null)
    chatState.setEditMessageContent('')
  }, [chatState])

  // Handle message editing
  const handleEditMessage = React.useCallback(async () => {
    if (!chatState.editingMessageId || !chatState.editMessageContent.trim()) return

    const originalMessage = chatContext.messages.find((msg: Message) => msg.id === chatState.editingMessageId)
    if (originalMessage && chatState.editMessageContent.trim() === originalMessage.content) {
      chatState.setEditingMessageId(null)
      chatState.setEditMessageContent('')
      return
    }

    if (chatState.editMessageContent.trim().length > 500) {
      toast.error(`Message cannot exceed 500 characters`)
      return
    }

    try {
      await chatContext.editMessage(chatState.editingMessageId, chatState.editMessageContent)
      chatState.setEditingMessageId(null)
      chatState.setEditMessageContent('')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to edit message:', error)
      toast.error('Failed to update message. Please try again.')
    }
  }, [chatState, chatContext])

  // Handle message deletion
  const handleDeleteMessage = React.useCallback(
    async (messageId: number, scope: DeleteMessageScope) => {
      try {
        // Check time limit only for "delete for everyone" option
        if (scope === DeleteMessageScope.FOR_EVERYONE) {
          const message = chatContext.messages.find((msg: Message) => msg.id === messageId)
          if (message) {
            const messageCreatedAt = new Date(message.createdAt).getTime()
            const currentTime = Date.now()
            const timeDifference = currentTime - messageCreatedAt

            if (timeDifference > MESSAGE.DELETE_TIME_LIMIT_MS) {
              toast.error('Messages can only be deleted for everyone within 5 minutes of being sent')
              return
            }
          }
        }

        chatState.setIsDeleting(true)
        await chatContext.deleteMessage(messageId, scope)
        chatState.setShowDeleteConfirmDialog(false)
        chatState.setDeletingMessageId(null)
        chatState.setIsDeleting(false)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete message:', error)
        toast.error('Failed to delete message. Please try again.')
        chatState.setShowDeleteConfirmDialog(false)
        chatState.setDeletingMessageId(null)
        chatState.setIsDeleting(false)
      }
    },
    [chatState, chatContext]
  )

  // Handle showing delete confirmation
  const handleShowDeleteConfirm = React.useCallback(
    (messageId: number) => {
      const message = chatContext.messages.find((msg: Message) => msg.id === messageId)
      if (!message) return

      if (message.sender.id !== authUser?.id) {
        toast.error('You can only delete your own messages')
        return
      }

      if (message.isSystem) {
        toast.error('System messages cannot be deleted')
        return
      }

      // Remove time limit check from here - let user see the dialog first
      chatState.setDeletingMessageId(messageId)
      chatState.setShowDeleteConfirmDialog(true)
    },
    [chatContext.messages, authUser?.id, chatState]
  )

  // Handle canceling delete message confirmation
  const handleCancelDelete = React.useCallback(() => {
    chatState.setShowDeleteConfirmDialog(false)
    chatState.setDeletingMessageId(null)
    chatState.setIsDeleting(false)
  }, [chatState])

  // Handle showing delete conversation confirmation
  const handleShowDeleteConversationConfirm = React.useCallback(
    (conversationId: number) => {
      chatState.setDeletingConversationId(conversationId)
      chatState.setShowDeleteConversationDialog(true)
    },
    [chatState]
  )

  // Handle canceling delete conversation confirmation
  const handleCancelDeleteConversation = React.useCallback(() => {
    chatState.setShowDeleteConversationDialog(false)
    chatState.setDeletingConversationId(null)
    chatState.setIsDeletingConversation(false)
  }, [chatState])

  // Handle deleting conversation
  const handleDeleteConversation = React.useCallback(
    async (permanent: boolean) => {
      if (!chatState.deletingConversationId) return

      try {
        chatState.setIsDeletingConversation(true)
        await chatContext.deleteConversation(chatState.deletingConversationId, permanent)

        if (chatState.selectedConversationId === chatState.deletingConversationId) {
          chatState.setSelectedConversationId(undefined)
        }

        chatState.setShowDeleteConversationDialog(false)
        chatState.setDeletingConversationId(null)
        chatState.setIsDeletingConversation(false)
        toast.success(permanent ? 'Conversation deleted permanently' : 'Conversation removed from your chat list')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to delete conversation:', error)
        toast.error(permanent ? 'Failed to delete conversation' : 'Failed to remove conversation')
        chatState.setShowDeleteConversationDialog(false)
        chatState.setDeletingConversationId(null)
        chatState.setIsDeletingConversation(false)
      }
    },
    [chatState, chatContext]
  )

  // Handle pinning conversation
  const handlePinConversation = React.useCallback(
    async (conversationId: number) => {
      try {
        await chatContext.pinConversation(conversationId)
        toast.success('Conversation pinned')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to pin conversation:', error)
        toast.error('Failed to pin conversation')
      }
    },
    [chatContext]
  )

  // Handle unpinning conversation
  const handleUnpinConversation = React.useCallback(
    async (conversationId: number) => {
      try {
        await chatContext.unpinConversation(conversationId)
        toast.success('Conversation unpinned')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to unpin conversation:', error)
        toast.error('Failed to unpin conversation')
      }
    },
    [chatContext]
  )

  // Handle unified search
  const handleUnifiedSearch = React.useCallback(async () => {
    if (!chatState.searchQuery.trim()) {
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      return
    }

    const query = chatState.searchQuery.toLowerCase()

    // Filter conversations from the conversation list
    const matchingConversations = chatContext.conversations.filter((conv: Conversation) => {
      // Check if conversation name matches
      const hasMatchingName = conv.name && conv.name.toLowerCase().includes(query)

      // Check if any participant matches
      const hasMatchingParticipant = conv.participants?.some(
        (participant: ConversationParticipant) =>
          participant.user.firstName.toLowerCase().includes(query) ||
          participant.user.lastName.toLowerCase().includes(query) ||
          (participant.user.email && participant.user.email.toLowerCase().includes(query)) ||
          `${participant.user.firstName} ${participant.user.lastName}`.toLowerCase().includes(query)
      )

      // Check if last message content matches
      const hasMatchingLastMessage = conv.lastMessageContent && conv.lastMessageContent.toLowerCase().includes(query)

      return hasMatchingName || hasMatchingParticipant || hasMatchingLastMessage
    })

    // Collect unique users from matching conversations
    const matchingUsers = new Map<number, ChatUser>()
    matchingConversations.forEach((conv: Conversation) => {
      conv.participants?.forEach((participant: ConversationParticipant) => {
        if (
          participant.user.firstName.toLowerCase().includes(query) ||
          participant.user.lastName.toLowerCase().includes(query) ||
          (participant.user.email && participant.user.email.toLowerCase().includes(query)) ||
          `${participant.user.firstName} ${participant.user.lastName}`.toLowerCase().includes(query)
        ) {
          matchingUsers.set(participant.user.id, {
            id: participant.user.id,
            firstName: participant.user.firstName,
            lastName: participant.user.lastName,
            email: participant.user.email,
            avatar: participant.user.avatar,
            profileId: participant.user.profileId,
          })
        }
      })
    })

    // Collect messages from matching conversations
    const matchingMessages: SearchResultMessage[] = []

    // Search messages in all conversations (not just selected one)
    chatContext.conversations.forEach((conv: Conversation) => {
      // Only search in conversations that have messages loaded or match the query
      if (conv.id === chatState.selectedConversationId || matchingConversations.some(mc => mc.id === conv.id)) {
        // For selected conversation, use loaded messages
        if (conv.id === chatState.selectedConversationId) {
          chatContext.messages.forEach((message: Message) => {
            if (
              message.content.toLowerCase().includes(query) ||
              `${message.sender.firstName} ${message.sender.lastName}`.toLowerCase().includes(query)
            ) {
              matchingMessages.push({
                id: message.id,
                content: message.content,
                sender: message.sender,
                createdAt: message.createdAt,
                conversationId: conv.id,
              })
            }
          })
        }
      }
    })

    // Search conversations globally using the API (including course type)
    const globalConversations = await chatContext.searchConversations(chatState.searchQuery)

    // Transform API conversations to match the expected format
    const transformedGlobalConversations = globalConversations
      .filter((conv: Conversation) => conv && conv.id) // Filter out invalid conversations
      .map((conv: Conversation) => ({
        id: conv.id,
        name: conv.name || '',
        description: conv.description || '',
        avatar: conv.avatar || '',
        type: conv.type || '',
        lastMessageAt: conv.lastMessageAt || '',
        lastMessageContent: conv.lastMessageContent || '',
        participants:
          conv.participants?.map((participant: ConversationParticipant) => ({
            id: participant.user.id,
            firstName: participant.user.firstName,
            lastName: participant.user.lastName,
            email: participant.user.email,
            avatar: participant.user.avatar || '',
            profileId: participant.user.profileId,
          })) || [],
      }))

    // Combine local and global conversations, removing duplicates
    const localTransformedConversations = matchingConversations
      .filter((conv: Conversation) => conv && conv.id) // Filter out invalid conversations
      .map((conv: Conversation) => ({
        id: conv.id,
        name: conv.name || '',
        description: conv.description || '',
        avatar: conv.avatar || '',
        type: conv.type || '',
        lastMessageAt: conv.lastMessageAt || '',
        lastMessageContent: conv.lastMessageContent || '',
        participants:
          conv.participants?.map((participant: ConversationParticipant) => ({
            id: participant.user.id,
            firstName: participant.user.firstName,
            lastName: participant.user.lastName,
            email: participant.user.email,
            avatar: participant.user.avatar || '',
            profileId: participant.user.profileId,
          })) || [],
      }))

    const allConversations = [...localTransformedConversations, ...transformedGlobalConversations]
    const uniqueConversations = allConversations.filter(
      (conv, index, self) => index === self.findIndex(c => c.id === conv.id)
    )

    chatState.setSearchResults({
      users: Array.from(matchingUsers.values()),
      messages: matchingMessages,
      conversations: uniqueConversations,
    })
  }, [chatState, chatContext])

  // Handle conversation name editing
  const handleStartEditConversationName = React.useCallback(
    (conversation: Conversation) => {
      chatState.setEditingConversationId(conversation.id)
      chatState.setEditingConversationName(conversation.name || '')
      chatState.setShowEditConversationDialog(true)
    },
    [chatState]
  )

  const handleSaveConversationName = React.useCallback(async () => {
    if (!chatState.editingConversationId) return

    try {
      await chatContext.updateConversation(chatState.editingConversationId, {
        name: chatState.editingConversationName.trim(),
      })
      chatState.setEditingConversationId(null)
      chatState.setEditingConversationName('')
      chatState.setShowEditConversationDialog(false)
      toast.success('Conversation name updated')
    } catch (error) {
      toast.error('Failed to update conversation name')
    }
  }, [chatState, chatContext])

  const handleCancelEditConversationName = React.useCallback(() => {
    chatState.setEditingConversationId(null)
    chatState.setEditingConversationName('')
    chatState.setShowEditConversationDialog(false)
  }, [chatState])

  // Handle message search result selection
  const handleMessageSearchResult = React.useCallback(
    (messageId: number, conversationId?: number) => {
      if (conversationId && conversationId !== chatState.selectedConversationId) {
        // Switch to the conversation first
        chatState.setSelectedConversationId(conversationId)

        // Scroll to the conversation in the list first
        if (scrollManager) {
          // Find the conversation type from the search results
          const conversation = chatContext.conversations.find(conv => conv.id === conversationId)
          const conversationType = conversation?.type
          // Add a small delay to ensure the conversation list has rendered
          setTimeout(() => {
            scrollManager.scrollToConversation(conversationId, conversationType)
          }, 100)
        }

        if (scrollManager) {
          scrollManager.beginMessageNavigationScroll()
          scrollManager.scrollToMessageWithDelay(messageId, 800)
        }
      } else {
        // Same conversation, scroll immediately
        if (scrollManager) {
          scrollManager.scrollToMessage(messageId)
        }
      }

      // Reset search state when selecting a message from search results
      chatState.setSearchQuery('')
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      chatState.setShowSearchDialog(false)
    },
    [chatState, scrollManager, chatContext.conversations]
  )

  // Handle pending target user
  const handlePendingTargetUser = React.useCallback(async () => {
    if (!chatState.pendingTargetUserId) return

    const existingConversation = findExistingConversation(chatState.pendingTargetUserId)

    if (existingConversation) {
      chatState.setSelectedConversationId(existingConversation.id)
      chatContext.setConversationId(existingConversation.id)
      chatState.setPendingTargetUserId(undefined)
      chatState.setHasCheckedExistingConversation(false)
    } else if (!chatContext.isCreatingConversation) {
      try {
        const newConversation = await chatContext.createConversation([chatState.pendingTargetUserId])
        if (newConversation) {
          chatState.setSelectedConversationId(newConversation.id)
          chatContext.setConversationId(newConversation.id)
        }
        chatState.setPendingTargetUserId(undefined)
        chatState.setHasCheckedExistingConversation(false)
      } catch (error) {
        toast.error('Failed to create conversation. Please try again.')
        chatState.setPendingTargetUserId(undefined)
        chatState.setHasCheckedExistingConversation(false)
      }
    }

    chatState.setHasCheckedExistingConversation(true)
  }, [chatState, chatContext, findExistingConversation])

  // Mark messages as read
  const markConversationAsRead = React.useCallback(async () => {
    const latestMessage = chatContext.messages[chatContext.messages.length - 1]
    const isLatestMessageFromCurrentUser = latestMessage && latestMessage.sender.id === authUser?.id
    const unreadCount = chatState.selectedConversationId
      ? chatContext.unreadCounts[chatState.selectedConversationId] || 0
      : 0

    if (!chatState.selectedConversationId || isLatestMessageFromCurrentUser || unreadCount === 0) return

    // Mark the entire conversation as read when opening chat
    try {
      await chatContext.markConversationAsRead(chatState.selectedConversationId)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to mark conversation as read when opening chat:', error)
    }
  }, [chatContext, chatState.selectedConversationId, authUser?.id])

  // Handle load more messages
  const handleLoadPreviousMessages = React.useCallback(() => {
    chatContext.loadPreviousMessages()
  }, [chatContext])

  const sendAttachmentMessages = React.useCallback(
    async (selectedFile: File, textContent: string) => {
      const filePlaceholder = getAttachmentPlaceholderContent(selectedFile)
      const captionContent = textContent.trim()
      const shouldSendTextSeparately = hasUserMessageText(captionContent)

      await chatContext.sendMessageWithFile(filePlaceholder, selectedFile)

      if (shouldSendTextSeparately) {
        await chatContext.sendMessage(captionContent)
      }
    },
    [chatContext]
  )

  // Handle send message
  const handleSendMessage = React.useCallback(async () => {
    if (chatState.selectedFile) {
      if (!chatState.selectedConversationId) {
        if (chatState.pendingTargetUserId) {
          try {
            const newConversation = await chatContext.createConversation([chatState.pendingTargetUserId])
            if (newConversation) {
              chatState.setSelectedConversationId(newConversation.id)
              chatState.setIsUploadingFile(true)
              try {
                const captionContent = chatState.newMessage
                const selectedFile = chatState.selectedFile

                chatState.setSelectedFile(null)
                chatState.setNewMessage('')

                try {
                  await sendAttachmentMessages(selectedFile, captionContent)

                  if (newConversation.id) {
                    markConversationAsRead()
                  }

                  toast.success('File sent successfully!')
                } finally {
                  chatContext.sendTypingIndicator(false)
                }
              } catch (error) {
                toast.error('Failed to send file. Please try again.')
              } finally {
                chatState.setIsUploadingFile(false)
              }
            }
          } catch (error) {
            toast.error('Failed to create conversation. Please try again.')
          }
          return
        }
        toast.info('Please select a conversation to send files')
        return
      }

      chatState.setIsUploadingFile(true)
      try {
        const captionContent = chatState.newMessage
        const selectedFile = chatState.selectedFile

        chatState.setSelectedFile(null)
        chatState.setNewMessage('')

        try {
          await sendAttachmentMessages(selectedFile, captionContent)

          if (chatState.selectedConversationId) {
            markConversationAsRead()
          }

          toast.success('File sent successfully!')
        } finally {
          chatContext.sendTypingIndicator(false)
        }
      } catch (error) {
        toast.error('Failed to send file. Please try again.')
      } finally {
        chatState.setIsUploadingFile(false)
      }
      return
    }

    if (!chatState.newMessage.trim()) return

    if (!chatState.selectedConversationId) {
      if (chatState.pendingTargetUserId) {
        try {
          const newConversation = await chatContext.createConversation([chatState.pendingTargetUserId])
          if (newConversation) {
            chatState.setSelectedConversationId(newConversation.id)

            // Clear input immediately for auto-paste behavior
            const messageContent = chatState.newMessage
            chatState.setNewMessage('')

            try {
              await chatContext.sendMessage(messageContent)

              // Mark conversation as read after sending message
              if (newConversation.id) {
                markConversationAsRead()
              }

              toast.success('New conversation created and message sent!')
            } finally {
              chatContext.sendTypingIndicator(false)
            }
          }
        } catch (error) {
          toast.error('Failed to create conversation. Please try again.')
        }
        return
      }

      toast.info('Please select a conversation from the sidebar or create a new one to send messages')
      return
    }

    // Clear the input immediately for auto-paste behavior
    const messageContent = chatState.newMessage
    chatState.setNewMessage('')

    try {
      // Send the message (which will show optimistically)
      await chatContext.sendMessage(messageContent)

      // Mark conversation as read after sending message
      if (chatState.selectedConversationId) {
        markConversationAsRead()
      }
    } finally {
      chatContext.sendTypingIndicator(false)
    }
  }, [chatState, chatContext, markConversationAsRead, sendAttachmentMessages])

  // Start replying to a message
  const handleStartReply = React.useCallback(
    (message: Message) => {
      chatState.setReplyingToMessage({
        id: message.id,
        content: message.content,
        sender: {
          id: message.sender.id,
          firstName: message.sender.firstName,
          lastName: message.sender.lastName,
          avatar: message.sender.avatar,
        },
        type: message.type,
        fileUrl: message.fileUrl,
        fileName: message.fileName,
      })
    },
    [chatState]
  )

  // Cancel reply
  const handleCancelReply = React.useCallback(() => {
    chatState.setReplyingToMessage(null)
  }, [chatState])

  // Handle reply message
  const handleReplyMessage = React.useCallback(async () => {
    if (!chatState.replyingToMessage || !chatState.newMessage.trim()) return

    if (!chatState.selectedConversationId) {
      toast.info('Please select a conversation to send reply')
      return
    }

    // Clear the input immediately for auto-paste behavior
    const messageContent = chatState.newMessage
    chatState.setNewMessage('')

    try {
      // Send the reply message
      await chatContext.sendReplyMessage(messageContent, chatState.replyingToMessage.id)

      // Mark conversation as read after sending reply
      if (chatState.selectedConversationId) {
        markConversationAsRead()
      }

      // Clear the reply state
      chatState.setReplyingToMessage(null)
    } finally {
      chatContext.sendTypingIndicator(false)
    }
  }, [chatState, chatContext, markConversationAsRead])

  // Handle key press
  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (chatState.replyingToMessage) {
          handleReplyMessage()
        } else {
          handleSendMessage()
        }
      }
    },
    [handleSendMessage, handleReplyMessage, chatState.replyingToMessage]
  )

  // Open chat
  const openChat = React.useCallback(
    (
      targetUserId?: number,
      targetUserName?: string,
      conversationId?: number,
      jobName?: string,
      targetUserAvatar?: string
    ) => {
      chatState.setIsOpen(true)
      chatState.setHasCheckedExistingConversation(false)
      chatState.setJobName(jobName || '')

      // Default to conversation list when opening without a specific target.
      // Jump straight to messages only if a target user or conversation is provided.
      const shouldJumpToMessages = !!(targetUserId || conversationId)
      chatState.setShowMobileConversationList(!shouldJumpToMessages)

      if (targetUserId) {
        chatState.setPendingTargetUserId(targetUserId)
        // Ensure targetUserName always has a meaningful value
        const userName = targetUserName?.trim() || `User ${targetUserId}`
        chatState.setTargetUserName(userName)
        chatState.setTargetUserAvatar(targetUserAvatar || '')
      }
      if (conversationId) {
        chatState.setSelectedConversationId(conversationId)
        chatContext.setConversationId(conversationId)
        if (!chatState.isMobileView) {
          markConversationAsRead()
        }

        scrollManager?.prepareConversationBottomScroll()

        // Scroll to the specific conversation in the list
        if (scrollManager) {
          // Find the conversation type from the conversations list
          const conversation = chatContext.conversations.find(conv => conv.id === conversationId)
          const conversationType = conversation?.type

          // Add a small delay to ensure the conversation list has rendered
          setTimeout(() => {
            scrollManager.scrollToConversation(conversationId, conversationType)
          }, 200)
        }
      }

      setTimeout(() => {
        // Use debounced version to prevent excessive API calls
        chatContext.debouncedLoadConversations()
      }, 100)
    },
    [chatState, chatContext, markConversationAsRead, scrollManager]
  )
  // Reset chat state
  const resetChatState = React.useCallback(() => {
    // Reset conversation and user state
    chatState.setHasCheckedExistingConversation(false)
    chatState.setPendingTargetUserId(undefined)
    chatState.setTargetUserName('')
    chatState.setTargetUserAvatar('')
    chatState.setJobName('')
    chatState.setSelectedConversationId(undefined)

    // Reset message input state
    chatState.setNewMessage('')
    chatState.setSelectedFile(null)
    chatState.setIsUploadingFile(false)

    // Reset search state
    chatState.setSearchQuery('')
    chatState.setSearchResults({ users: [], messages: [], conversations: [] })
    chatState.setShowSearchDialog(false)
    chatState.setShowMessageSearch(false)
    chatState.setShowUserSearch(false)

    // Reset dialog states
    chatState.setShowNewConversationDialog(false)
    chatState.setShowCreateGroupChatDialog(false)
    chatState.setShowEditConversationDialog(false)
    chatState.setShowDeleteConfirmDialog(false)
    chatState.setShowDeleteConversationDialog(false)

    // Reset editing states
    chatState.setEditingMessageId(null)
    chatState.setEditingConversationId(null)
    chatState.setEditingConversationName('')
    chatState.setEditMessageContent('')
    chatState.setNewConversationName('')

    // Reset user selection states
    chatState.setSelectedUsers([])

    // Reset deletion states
    chatState.setDeletingMessageId(null)
    chatState.setIsDeleting(false)
    chatState.setDeletingConversationId(null)
    chatState.setIsDeletingConversation(false)

    // Reset mobile view states
    chatState.setShowMobileConversationList(true)

    // Reset previous message count
    chatState.setPreviousMessageCount(0)
  }, [chatState])

  // Get filtered conversations
  const getFilteredConversations = React.useCallback(() => {
    return chatState.filteredConversations
  }, [chatState.filteredConversations])

  // Get filtered messages
  const getFilteredMessages = React.useCallback(() => {
    return chatState.filteredMessages
  }, [chatState.filteredMessages])

  // Handle conversation selection from search results
  const handleConversationSelectFromSearch = React.useCallback(
    (conversationId: number) => {
      chatState.setSelectedConversationId(conversationId)
      chatContext.setConversationId(conversationId)

      // Auto-set isConversationRead to true for both desktop and mobile views
      chatState.setIsConversationRead(true)

      // Scroll to the conversation in the list
      if (scrollManager) {
        // Find the conversation type from the search results
        const conversation = chatContext.conversations.find(conv => conv.id === conversationId)
        const conversationType = conversation?.type
        // Add a small delay to ensure the conversation list has rendered
        setTimeout(() => {
          scrollManager.scrollToConversation(conversationId, conversationType)
        }, 100)
      }

      // Reset search state when selecting a conversation from search results
      chatState.setSearchQuery('')
      chatState.setSearchResults({ users: [], messages: [], conversations: [] })
      chatState.setShowSearchDialog(false)
    },
    [chatState, chatContext, scrollManager]
  )

  return {
    // Actions
    handleMessageSenderClick,
    handleSelectConversation,
    handleCreateConversation,
    handleUserSelect,
    handleUserRemove,
    handleUserProfileNavigation,
    handleUserSearchSelect,
    handleStartEditMessage,
    handleCancelEditMessage,
    handleEditMessage,
    handleDeleteMessage,
    handleShowDeleteConfirm,
    handleCancelDelete,
    handleShowDeleteConversationConfirm,
    handleCancelDeleteConversation,
    handleDeleteConversation,
    handlePinConversation,
    handleUnpinConversation,
    handleUnifiedSearch,
    handleConversationSelectFromSearch,
    handleStartEditConversationName,
    handleSaveConversationName,
    handleCancelEditConversationName,
    handleMessageSearchResult,
    handlePendingTargetUser,
    markConversationAsRead,
    handleLoadPreviousMessages,
    handleSendMessage,
    handleReplyMessage,
    handleStartReply,
    handleCancelReply,
    openChat,
    closeChat,
    resetChatState,

    // Getters
    getChatTitle,
    getTargetedUser,
    getConversationTitle,
    formatTimestamp,
    renderFileMessage,
    getFilteredConversations,
    getFilteredMessages,

    // Emoji handlers
    insertEmoji,
    insertCustomEmoji,

    // Input handlers
    handleTyping,
    handleInputFocus,
    handleKeyPress,

    // Refs
    inputRef,
  }
}
